import {List, OrderedMap, Map, Set} from 'immutable';
import {get, clamp, reduce} from 'lodash';

import {weakMemo} from '@packages/utilities/memo';
import {DEFAULT_MEDIA_QUERIES} from '@packages/systems/style-legacy/media-queries';
import {cssifyName} from '@packages/systems/core/utils/TextUtils';
import {getIdWithoutInstance} from '@packages/systems/core/utils/NativeIdUtils';
import {type StyleBlock} from '@packages/systems/style-legacy/records';
import {
  getQuickActionLists,
  formatEventForQuickEvent,
  isPresetEvent,
} from './quick-effects';
import type {VariableEntries} from '.';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {Plugins} from '@packages/systems/wfdl';
import {
  getUserlandPrefix,
  toClassName,
} from '@packages/systems/style-legacy/selectors';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {getNameFromClassNameMap} from '@packages/systems/style-legacy/utils';
import {ClassNameMap} from '@packages/systems/style/types';

const eventActionListIdPath = 'action.config.actionListId';

type StyleBlockGuid = string;
type StyleBlockName = string;
type StyleBlockMap = Map<string, StyleBlock>;
type SiteStyleTypes = Array<{
  _id: StyleBlockGuid;
  data: {
    name: StyleBlockName;
    origin?: Qualified<StyleBlockGuid> | null;
  };
}>;

/*
.*-------------------------------
 * Helpers
 *-------------------------------
*/

// @ts-expect-error - TS7006 - Parameter 'v' implicitly has an 'any' type.
const isEmpty = (v) => {
  if (v == null) {
    return true;
  }

  if (typeof v === 'string' || Array.isArray(v)) {
    return v.length === 0;
  }

  if (typeof v === 'object') {
    switch (v.constructor) {
      case List:
      case Map:
      case OrderedMap:
      case Set:
        return v.size === 0;
    }
  }

  return false;
};

const getClassName = weakMemo((classMap: ClassNameMap) => (guid: string) => {
  const styleBlockName = getNameFromClassNameMap(classMap, guid);
  return cssifyName(styleBlockName ? styleBlockName : '');
});

const getSelector = (
  classMap: ClassNameMap,
  selectorGuids: string[] | undefined
) => {
  const selectorClassNames = selectorGuids
    ? selectorGuids.map(getClassName(classMap))
    : [];

  // If guid is part of a combo class but one of the class is missing,
  // set the entire label to null to avoid accidentally
  // selecting an unintended class.
  return selectorClassNames.some(isEmpty) || selectorClassNames.length === 0
    ? null
    : `.${selectorClassNames.join('.')}`;
};

const reduceAffected = weakMemo(
  weakMemo((classMap: ClassNameMap) =>
    // @ts-expect-error - TS7006 - Parameter 'eventTarget' implicitly has an 'any' type.
    weakMemo((eventTarget) =>
      // @ts-expect-error - TS7006 - Parameter 'affectedElements' implicitly has an 'any' type.
      weakMemo((affectedElements) =>
        reduce(
          affectedElements,
          // @ts-expect-error - TS2322 - Type '{}' is not assignable to type 'T[keyof T]'.
          (newElements, {limitAffectedElements, selector} = {}, id: string) => {
            // When selector is missing, fall back to event target id
            if (!selector && eventTarget) {
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
              newElements[id] = {id: eventTarget.id};
              return newElements;
            }

            const selectorGuids = get(selector, 'selectorGuids');
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
            newElements[id] = {
              selector: getSelector(classMap, selectorGuids),
              selectorGuids,
              limitAffectedElements,
            };

            return newElements;
          },
          {}
        )
      )
    )
  )
);

// @ts-expect-error - TS7006 - Parameter 'classMap' implicitly has an 'any' type.
const updateEventTarget = weakMemo((classMap) =>
  // @ts-expect-error - TS7031 - Binding element 'appliesTo' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'styleBlockIds' implicitly has an 'any' type.
  weakMemo(({appliesTo, id, styleBlockIds}) => ({
    selector: styleBlockIds.length
      ? getSelector(classMap, styleBlockIds)
      : null,
    originalId: getIdWithoutInstance(id),
    appliesTo,
  }))
);

const formatAffectedElements = weakMemo(
  // @ts-expect-error - TS7006 - Parameter 'eventTarget' implicitly has an 'any' type.
  weakMemo((eventTarget) =>
    weakMemo((classMap: ClassNameMap) =>
      // @ts-expect-error - TS7006 - Parameter 'affectedElements' implicitly has an 'any' type.
      weakMemo((affectedElements) =>
        Object.keys(affectedElements).length > 0
          ? reduceAffected(classMap)(eventTarget)(affectedElements)
          : affectedElements
      )
    )
  )
);

const formatEvent = weakMemo((classMap: ClassNameMap) =>
  weakMemo((event: any): any => {
    if (
      event?.action?.actionTypeId == null &&
      event?.action?.config?.actionListId == null
    ) {
      return undefined;
    }

    if (event.action) {
      if (isPresetEvent(event)) {
        event = formatEventForQuickEvent(event);
      }

      const {id, actionTypeId, config} = event.action;

      if (event.action.instant) {
        event = {
          ...event,
          action: {id, actionTypeId, config: {...config, duration: 0}},
        };
      }

      if (config.affectedElements != null) {
        event = {
          ...event,
          action: {
            ...event.action,
            config: {
              ...event.action.config,
              affectedElements: formatAffectedElements(event.target)(classMap)(
                config.affectedElements
              ),
            },
          },
        };
      }
    }

    if (event.target.appliesTo === 'CLASS') {
      event = {
        ...event,
        target: updateEventTarget(classMap)(event.target),
      };
    }

    if (Array.isArray(event.targets)) {
      event = {
        ...event,
        // @ts-expect-error - TS7006 - Parameter 'target' implicitly has an 'any' type.
        targets: event.targets.map((target) =>
          target.appliesTo === 'CLASS'
            ? updateEventTarget(classMap)(target)
            : target
        ),
      };
    }

    return event;
  })
);

// @ts-expect-error - TS7006 - Parameter 'target' implicitly has an 'any' type.
const transformActionItemTarget = (target, classMap: any) => {
  if (!target) {
    return null;
  }
  if (typeof target === 'string') {
    return {
      id: target,
    };
  }

  const {
    pluginElement,
    objectId,
    nodeId,
    appliesTo,
    boundaryMode,
    useEventTarget,
    styleBlockIds,
  } = target;

  const base = {
    pluginElement,
    objectId,
    useEventTarget,
  } as const;

  switch (appliesTo) {
    case undefined:
    case 'ELEMENT': {
      return {
        ...base,
        boundaryMode: boundaryMode || undefined,
        id: nodeId,
      };
    }
    case 'TRIGGER_ELEMENT': {
      return {
        ...base,
        id: nodeId,
        useEventTarget: true,
      };
    }
    case 'ELEMENT_CLASS': {
      const selector = styleBlockIds && getSelector(classMap, styleBlockIds);

      if (!selector) {
        return {};
      }
      return {
        ...base,
        selector,

        // rered for server-side rendering
        selectorGuids: styleBlockIds,
      };
    }
  }
};

// @ts-expect-error - TS7006 - Parameter 'classMap' implicitly has an 'any' type.
const transformItems = weakMemo((classMap) =>
  // @ts-expect-error - TS7006 - Parameter 'actionItem' implicitly has an 'any' type.
  weakMemo((actionItem) => {
    const {actionTypeId, config: oldConfig, id, instant} = actionItem;

    let config = {
      ...oldConfig,
      duration: instant ? 0 : oldConfig.duration,
      target: transformActionItemTarget(actionItem.config.target, classMap),
    };

    if (actionTypeId === 'STYLE_OPACITY') {
      config = {
        ...config,
        value: clamp((config.value == null ? 100 : config.value) / 100, 0, 1),
        unit: '',
      };
    }

    return {id, actionTypeId, config};
  })
);

const mapItemGroup = weakMemo(
  // @ts-expect-error - TS7006 - Parameter 'classMap' implicitly has an 'any' type.
  weakMemo((classMap) =>
    // @ts-expect-error - TS7031 - Binding element 'actionItems' implicitly has an 'any' type.
    weakMemo(({actionItems, ...actionGroup}) => ({
      ...actionGroup,
      actionItems: actionItems.map(transformItems(classMap)),
    }))
  )
);

const mapContinuousParameterGroups = weakMemo(
  // @ts-expect-error - TS7006 - Parameter 'classMap' implicitly has an 'any' type.
  weakMemo((classMap) =>
    // @ts-expect-error - TS7031 - Binding element 'continuousActionGroups' implicitly has an 'any' type.
    weakMemo(({continuousActionGroups, ...paramGroup}) => ({
      ...paramGroup,
      continuousActionGroups: continuousActionGroups.map(
        mapItemGroup(classMap)
      ),
    }))
  )
);

// @ts-expect-error - TS7006 - Parameter 'classMap' implicitly has an 'any' type.
const formatActionList = weakMemo((classMap) =>
  // @ts-expect-error - TS7006 - Parameter 'actionList' implicitly has an 'any' type.
  weakMemo((actionList) => {
    if (Array.isArray(actionList.continuousParameterGroups)) {
      return {
        ...actionList,
        continuousParameterGroups: actionList.continuousParameterGroups.map(
          mapContinuousParameterGroups(classMap)
        ),
      };
    } else if (Array.isArray(actionList.actionItemGroups)) {
      return {
        ...actionList,
        actionItemGroups: actionList.actionItemGroups.map(
          mapItemGroup(classMap)
        ),
      };
    }
    return actionList;
  })
);

const filterMapEventsAndActionLists = weakMemo((classMap: ClassNameMap) =>
  weakMemo((actionLists: any) =>
    weakMemo((events: any) => {
      const transformActionList = (actionList: any) =>
        formatActionList(classMap)(actionList);
      const transformEvent = (event: any) => formatEvent(classMap)(event);

      // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
      const rawActionListsMap = actionLists.reduce<Record<string, any>>(
        // @ts-expect-error - TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'actionList' implicitly has an 'any' type.
        (result, actionList) => {
          result[actionList.id] = actionList;
          return result;
        },
        {}
      );

      const {eventsMap, actionListsMap, quickEffectsEvents} = events.reduce(
        // @ts-expect-error - TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'event' implicitly has an 'any' type.
        (result, event) => {
          // Event won't trigger since all queries are off — filter out the event
          if (event == null || event.mediaQueries.length === 0) {
            return result;
          }

          // Action list for quick effect event is hardcoded and loaded automatically
          // based on the type of quick effect, so include event in filter
          if (event && event.action && isPresetEvent(event)) {
            const formattedEvent = transformEvent(event);
            result.eventsMap[event.id] = formattedEvent;
            result.quickEffectsEvents.push(formattedEvent);

            return result;
          }

          const actionListIdForEvent = get(event, eventActionListIdPath);
          const actionList = rawActionListsMap[actionListIdForEvent];

          // Action list wasn't found, ignore event
          if (actionList == null) {
            return result;
          }

          result.eventsMap[event.id] = transformEvent(event);

          // Include the action lists for the event in order to play the animation.
          // Exclude all action lists that are not linked with any event to save
          // on bundle size
          if (!result.actionListsMap[actionListIdForEvent]) {
            result.actionListsMap[actionListIdForEvent] =
              transformActionList(actionList);
          }

          return result;
        },
        {eventsMap: {}, actionListsMap: {}, quickEffectsEvents: []}
      );

      const allActionListsMap = {
        ...actionListsMap,
        ...getQuickActionLists(quickEffectsEvents),
      } as const;

      return {allActionListsMap, eventsMap};
    })
  )
);

const serializeIx2SiteData = () => ({
  mediaQueries: DEFAULT_MEDIA_QUERIES.map(({key, min, max}) => ({
    key,
    min,
    max,
  })),
});

/**
 * Given all needed data (in the arguments) provide IX2 data in the format that is needed for IX2 being
 * initialized in published sites using window.Webflow.require('ix2').init.
 */
const serializeIx2Data = weakMemo(
  ({
    events,
    actionLists,
    classMap,
    variableEntries,
  }: {
    events: any;
    actionLists: any;
    classMap: ClassNameMap;
    variableEntries: VariableEntries;
  }) => {
    const {eventsMap, allActionListsMap} =
      filterMapEventsAndActionLists(classMap)(actionLists)(events);

    const processedActionLists = transformActionListsWithCssVariableNames(
      allActionListsMap,
      variableEntries
    );

    return {
      events: eventsMap,
      actionLists: processedActionLists,
      site: serializeIx2SiteData(),
    };
  }
);

const transformActionListsWithCssVariableNames = (
  actionLists: any,
  variableEntries: VariableEntries
) => {
  if (!variableEntries) {
    throw new Error(
      `Invalid variableEntries passed to transformActionListsWithCssVariableNames`
    );
  }
  if (Object.keys(variableEntries).length === 0) {
    return actionLists;
  }

  return branchObjectAndReplaceVariables({
    obj: actionLists,
    reference: variableEntries,
  });
};

type NestedObject =
  | {
      [key: string]: any;
    }
  | any[];

function branchObjectAndReplaceVariables({
  obj,
  reference,
}: {
  obj: NestedObject;
  reference: VariableEntries;
}): NestedObject {
  let newObj;

  if (Array.isArray(obj)) {
    newObj = [];
    for (let i = 0; i < obj.length; i++) {
      const value = obj[i];
      if (typeof value === 'object' && value !== null) {
        newObj[i] = branchObjectAndReplaceVariables({obj: value, reference});
      } else {
        newObj[i] = value;
      }
    }
  } else {
    newObj = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        newObj[key] = branchObjectAndReplaceVariables({obj: value, reference});
      } else if (
        (key === 'objectId' || key === 'globalSwatchId') &&
        typeof value === 'string' &&
        StyleVariables.isVariablePlaceholder(value)
      ) {
        const variableId = StyleVariables.getVariableIdFromPlaceholder(value);
        const newValue = variableId && reference[variableId];
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"objectId"' can't be used to index type '{}'.
        newObj[key] = newValue || value;
      } else {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        newObj[key] = value;
      }
    }
  }
  return newObj;
}

const styleBlocksToClassMap_ = weakMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((styleBlocks: StyleBlockMap): ClassNameMap => {
      return styleBlocks.reduce<Record<string, any>>((result, styleBlock) => {
        const styleBlockName = styleBlock.name;
        const userlandPrefix = styleBlock.origin
          ? getUserlandPrefix(styleBlock.origin, slugMap)
          : null;
        result[styleBlock._id] = toClassName(
          styleBlockName ? styleBlockName : '',
          {
            userlandPrefix,
          }
        );
        return result;
      }, {});
    })
);

const styleBlocksToClassMap = ({
  slugMap,
  styleBlocks,
}: {
  slugMap: Plugins.PluginsType['slugMap'];
  styleBlocks: StyleBlockMap;
}) => {
  return styleBlocksToClassMap_(slugMap)(styleBlocks);
};

const siteStyleTypesToClassMap_ = weakMemo(
  (slugMap: Plugins.PluginsType['slugMap']) =>
    weakMemo((siteStyles: SiteStyleTypes): ClassNameMap => {
      return siteStyles.reduce<Record<string, any>>((result, styleBlock) => {
        const styleBlockName = styleBlock.data.name;
        const userlandPrefix = styleBlock.data.origin
          ? getUserlandPrefix(styleBlock.data.origin, slugMap)
          : null;
        result[styleBlock._id] = toClassName(
          styleBlockName ? styleBlockName : '',
          {
            userlandPrefix,
          }
        );
        return result;
      }, {});
    })
);

const siteStyleTypesToClassMap = ({
  slugMap,
  siteStyles,
}: {
  slugMap: Plugins.PluginsType['slugMap'];
  siteStyles: SiteStyleTypes;
}) => {
  return siteStyleTypesToClassMap_(slugMap)(siteStyles);
};

export {
  serializeIx2Data,
  formatEvent,
  styleBlocksToClassMap,
  siteStyleTypesToClassMap,
};
